import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useDREData, type DREDataParams, type DRECalculations } from '@/app/financial/dre/hooks/useDREData'
import { getClients, getConsolidatedData, getConsolidatedMonthly } from '@/api/sig-data'
import { useGetOfficialProa } from '@/app/financial/proa/hooks/useProaData'
import type { ClientResultApiResponse } from '@/types/financial'

/**
 * Combined data for Painel Gerencial dashboard
 */
export interface PainelGerencialData {
  // KPI Data from DRE
  kpis: {
    receita: { real: number; proa: number }
    receitaFinanceira: { real: number; proa: number }
    folha: { real: number; proa: number }
    custosDiretos: { real: number; proa: number }
    custosADM: { real: number; proa: number }
    impostos: { real: number; proa: number }
    ebitda: { real: number; proa: number }
  }

  // Client ranking data
  clients: ClientResultApiResponse[]

  // Monthly time-series data
  monthlyData: Array<{ month: string; receita: number; custos: number }>

  // Raw calculations for charts
  calculations: DRECalculations

  // Metadata
  lastUpdated: string
}

/**
 * Parameters for fetching Painel Gerencial data
 */
export interface PainelGerencialParams {
  year: number
  month?: number
}

/**
 * Query key factory for Painel Gerencial data
 */
export const painelGerencialKeys = {
  all: ['painelGerencial'] as const,
  data: (params: PainelGerencialParams) => ['painelGerencial', 'data', params] as const
}

/**
 * Fetch combined data for Painel Gerencial dashboard
 * Combines DRE data (for KPIs and calculations) with client ranking data
 */
const fetchPainelGerencialData = async (params: PainelGerencialParams): Promise<PainelGerencialData> => {
  try {
    console.log('[Painel Gerencial] Fetching data for year:', params.year)

    // Fetch DRE data for KPIs and calculations
    const dreParams: DREDataParams = params.month
      ? { mode: 'monthly', year: params.year, month: params.month }
      : { mode: 'yearly', year: params.year }

    // Parallel fetch of DRE, client data, and monthly time-series
    const [dreResponse, clientsData, monthlyData] = await Promise.all([
      // Fetch consolidated data - pass month as number string for monthly, undefined for yearly
      getConsolidatedData(params.year.toString(), params.month ? params.month.toString() : undefined),
      getClients(params.year.toString(), { limit: 50 }),
      getConsolidatedMonthly(params.year.toString()).catch(() => [])
    ])

    console.log('[Painel Gerencial] Fetched data - Clients:', clientsData?.length, 'Monthly:', monthlyData?.length)

    // Extract KPI data from consolidated response - NO FALLBACKS, only real data or zero
    const kpis = {
      receita: {
        real: dreResponse?.data?.receita?.real ?? 0,
        proa: dreResponse?.data?.receita?.proa ?? 0
      },
      receitaFinanceira: {
        real: dreResponse?.data?.receitaFinanceira?.real ?? 0,
        proa: dreResponse?.data?.receitaFinanceira?.proa ?? 0
      },
      folha: {
        real: dreResponse?.data?.folha?.real ?? 0,
        proa: dreResponse?.data?.folha?.proa ?? 0
      },
      custosDiretos: {
        real: dreResponse?.data?.custosDiretos?.real ?? 0,
        proa: dreResponse?.data?.custosDiretos?.proa ?? 0
      },
      custosADM: {
        real: dreResponse?.data?.despesas?.real ?? 0,
        proa: dreResponse?.data?.despesas?.proa ?? 0
      },
      impostos: {
        real: dreResponse?.data?.impostos?.real ?? 0,
        proa: dreResponse?.data?.impostos?.proa ?? 0
      },
      ebitda: {
        real: (dreResponse?.data?.lucroLiquido?.real ?? 0) + (dreResponse?.data?.impostos?.real ?? 0),
        proa: (dreResponse?.data?.lucroLiquido?.proa ?? 0) + (dreResponse?.data?.impostos?.proa ?? 0)
      }
    }

    const finalClientsArray = Array.isArray(clientsData) ? clientsData : []

    return {
      kpis,
      clients: finalClientsArray,
      monthlyData: Array.isArray(monthlyData) ? monthlyData : [],
      calculations: {
        receitaBruta: dreResponse?.data?.faturamento?.real ?? 0,
        receitaLiquida: dreResponse?.data?.receita?.real ?? 0,
        lucroBruto: (dreResponse?.data?.receita?.real ?? 0) - (dreResponse?.data?.despesas?.real ?? 0),
        lucroLiquido: dreResponse?.data?.lucroLiquido?.real ?? 0,
        margemBruta: (dreResponse?.data?.receita?.real ?? 0) > 0
          ? (((dreResponse.data.receita.real ?? 0) - (dreResponse.data.despesas.real ?? 0)) / dreResponse.data.receita.real) * 100
          : 0,
        margemLiquida: (dreResponse?.data?.receita?.real ?? 0) > 0
          ? ((dreResponse.data.lucroLiquido.real ?? 0) / dreResponse.data.receita.real) * 100
          : 0
      },
      lastUpdated: new Date().toISOString()
    }
  } catch (error) {
    console.error('Failed to fetch Painel Gerencial data:', error)
    console.error('Error details:', error)
    throw error
  }
}

/**
 * Helper function to convert month number to month name
 */
function getMonthName(month: number): string {
  const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
  return months[month - 1]
}

/**
 * Custom hook for Painel Gerencial data
 * Fetches and combines DRE data and client rankings for the management dashboard
 *
 * @param params - Year and optional month for data filtering
 * @returns Query result with combined dashboard data
 */
export const usePainelGerencialData = (params: PainelGerencialParams) => {
  const enabled = !!params.year
  const year = params.year || new Date().getFullYear() // Provide default to ensure hook is always called with a value

  // Fetch official PROA from DynamoDB (same source as big-numbers page)
  // Must be called before any conditional returns to maintain hook order
  const { data: officialProa, isLoading: isProaLoading } = useGetOfficialProa(year)

  // Fetch base data (real values, clients, monthly)
  const baseQuery = useQuery({
    queryKey: painelGerencialKeys.data(params),
    queryFn: () => fetchPainelGerencialData(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled,
    retry: 3
  })

  // Create a map of DRE line item to PROA annual target
  const proaMap = useMemo(() => {
    const map = new Map<string, number>()
    if (officialProa?.metrics) {
      officialProa.metrics.forEach(proa => {
        map.set(proa.lineItem, proa.annual)
      })
    }
    return map
  }, [officialProa?.metrics])

  // Merge PROA values into the base data
  const dataWithProa = useMemo(() => {
    if (!baseQuery.data) return null

    return {
      ...baseQuery.data,
      kpis: {
        receita: {
          real: baseQuery.data.kpis.receita.real,
          proa: proaMap.get('ReceitaBruta') ?? 0
        },
        receitaFinanceira: {
          real: baseQuery.data.kpis.receitaFinanceira.real,
          proa: proaMap.get('ReceitasFinanceiras') ?? 0
        },
        folha: {
          real: baseQuery.data.kpis.folha.real,
          proa: proaMap.get('Folha') ?? 0
        },
        custosDiretos: {
          real: baseQuery.data.kpis.custosDiretos.real,
          proa: proaMap.get('ServicosPrestados') ?? 0
        },
        custosADM: {
          real: baseQuery.data.kpis.custosADM.real,
          proa: proaMap.get('DespesasOperacionais') ?? 0
        },
        impostos: {
          real: baseQuery.data.kpis.impostos.real,
          proa: (proaMap.get('ISS') ?? 0) + (proaMap.get('PIS') ?? 0) + (proaMap.get('COFINS') ?? 0)
        },
        ebitda: {
          real: baseQuery.data.kpis.ebitda.real,
          proa: proaMap.get('LucroOperacional') ?? 0
        }
      }
    }
  }, [baseQuery.data, proaMap])

  return {
    ...baseQuery,
    data: dataWithProa,
    isLoading: baseQuery.isLoading || isProaLoading
  }
}
